import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import './SidePanel.css'
import { Button as Btn, ButtonGroup } from 'react-bootstrap'
import Details from './Details.component'
import History from './History.component'
import Treatment from './Treatment.component'
import Insurance from './Insurance'
import FilesTab from './FilesTab'
import FamilyMembers, { genderIcon } from './FamilyMembers'
import PaymentMethods from './PaymentMethod'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { Patient } from '@dataTypes/PatientInfo.types'
import CallerModal from '../Modals/CallerModal/CallerModal.component'
import EmailSender from '../Modals/EmailSender/EmailSender.component'
import { ScheduleAppointment, SelectContactModal } from '../Modals'
import moment from 'moment'
import {
  AppointmentRequest,
  PatientWithInsurance,
} from '@dataTypes/appointment.type'
import { createAppointment } from '@services/api/appointment.api'
import AddPatientModal from '../Modals/AddPatientModal/AddPatientModal.component'
import ConfirmDeleteModal from '../Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import {
  updatePatient,
  removePatient,
  fetchAllergiesCatalog,
  fetchMedicalConditionsCatalog,
} from '@store/actions'
import {
  closeSidePanel,
  toggleSidePanel,
} from '@store/reducers/uiSlice.reducer'
import {
  refreshFromPatient,
  setSelectedPatient,
} from '@store/reducers/patientmanagement.reducer'
import { fetchPatientById } from '@services/api/patient.api'
import { getFullName } from '@utils/fullname.util'
import { maskString } from '@utils/mask-string'
import { IContact } from '@dataTypes/contact.type'
import useContact from '@components/pages/Messages/MessageCenterContact/useContact'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import { Tooltip } from '@enamelai/design-system'
import EditPhoto from '../Modals/EditPhoto/EditPhoto.component'
import {
  getPatientAllergyTitles,
  getPatientMedicalConditionTitles,
} from '@utils/patient-allergies-conditions.util'
import Avatar from '@components/common/Avatar/Avatar.component'
import { ReactComponent as InsuranceVerifiedIcon } from '@assets/images/Sidepanel/insurance-verified.svg'
import { ReactComponent as CertificateVerifiedIcon } from '@assets/images/Sidepanel/certificate-verified.svg'
import { ReactComponent as CalendarAddIcon } from '@assets/images/Sidepanel/calendar-add.svg'
import { ReactComponent as PhoneIcon } from '@assets/images/Sidepanel/phone.svg'
import { ReactComponent as TreatmentTabIcon } from '@assets/images/Sidepanel/treatment-tab.svg'
import { ReactComponent as FamilyMembersTabIcon } from '@assets/images/Sidepanel/family-members-tab.svg'
import { ReactComponent as InsuranceTabIcon } from '@assets/images/Sidepanel/insurance-plan-overview.svg'
import { ReactComponent as PaymentWalletTabIcon } from '@assets/images/Sidepanel/payment-wallet-tab.svg'
import { ReactComponent as PanelCloseIcon } from '@assets/images/Sidepanel/panel-close.svg'
import { ReactComponent as ArrowExpandIcon } from '@assets/images/Sidepanel/arrow-expand.svg'
import { noteOverlayAppModalBootstrapProps } from '@components/common/NoteEditorOverlay'
import { navigateToPatientProfile } from '@utils/patientProfileNavigation.util'
import {
  isMockPatientId,
  shouldUsePatientTrayMockData,
} from '@utils/patientTrayMocks.util'
import { getMembershipOverviewForMember } from '@constants/Membership/membershipPage.constants'
import { getMembershipMembers } from '@store/actions/membership.action'
import {
  setMembershipSidePanelOverview,
  useMembershipSidePanelOverview,
} from '@store/membershipSidePanelOverview.store'

type SidePanelProps = {
  isHidden: boolean
  toggle: () => void
}

enum Tabs {
  DETAILS = 'details',
  HISTORY = 'history',
  PLAN = 'plan',
  INSURANCE = 'insurance',
  FILES = 'files',
  FAMILY_MEMBERS = 'family-members',
  PAYMENT_METHODS = 'payment-methods',
}

const listofTabs = [
  {
    name: Tabs.DETAILS,
    unselected: 'mgc_user_3_line',
    selected: 'mgc_user_3_fill',
    tooltip: 'Details',
  },
  {
    name: Tabs.HISTORY,
    unselected: 'mgc_history_anticlockwise_line',
    selected: 'mgc_history_anticlockwise_fill',
    tooltip: 'Appointments',
  },
  {
    name: Tabs.PLAN,
    unselected: 'mgc_task_line',
    selected: 'mgc_task_fill',
    tooltip: 'Treatment plan',
    SvgIcon: TreatmentTabIcon,
  },
  {
    name: Tabs.INSURANCE,
    unselected: 'mgc_shield_line',
    selected: 'mgc_shield_fill',
    tooltip: 'Insurance',
    SvgIcon: InsuranceTabIcon,
  },
  {
    name: Tabs.FILES,
    unselected: 'mgc_documents_line',
    selected: 'mgc_documents_fill',
    tooltip: 'Files',
  },
  {
    name: Tabs.FAMILY_MEMBERS,
    unselected: 'mgc_group_2_line',
    selected: 'mgc_group_2_fill',
    tooltip: 'Family members',
    SvgIcon: FamilyMembersTabIcon,
  },
  {
    name: Tabs.PAYMENT_METHODS,
    unselected: 'mgc_wallet_3_line',
    selected: 'mgc_wallet_3_fill',
    tooltip: 'Payment methods',
    SvgIcon: PaymentWalletTabIcon,
  },
]

const getAge = (dob: string): number => {
  const birthDate = new Date(dob)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const hasHadBirthdayThisYear =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate())

  if (!hasHadBirthdayThisYear) {
    age--
  }

  return age
}

const SidePanel: React.FC<SidePanelProps> = ({
  isHidden,
  toggle,
}: SidePanelProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [selectedTab, setSelectedTab] = useState<Tabs>(Tabs.DETAILS)
  const [showEditPhotoModal, setShowEditPhotoModal] = useState(false)
  const [showCallerModal, setShowCallerModal] = useState(false)
  const [showEmailSender, setShowEmailSender] = useState(false)
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] =
    useState<boolean>(false)
  const [showPatientModal, setShowPatientModal] = useState(false)
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
  const [isPatientDetailsLoading, setIsPatientDetailsLoading] = useState(false)
  const { selectedPatient, refresh } = useAppSelector((state) => state.patient)
  const { ...contactsHooks } = useContact()
  const contacts = useAppSelector((state) => state.contacts.data)
  const isHIPAAView = useAppSelector((state) => state.ui.isHIPAAView)

  const hasMeaningfulValue = (value?: string | null): boolean => {
    if (!value) return false
    const cleaned = value.trim().toLowerCase()
    if (!cleaned) return false
    const placeholders = ['none', 'n/a', 'na', 'nil', 'null', '-']
    return !placeholders.includes(cleaned)
  }

  const membershipOverview = useMembershipSidePanelOverview()
  const certificateTooltipContent = membershipOverview?.tier || 'Certificate'

  const getPatientDisplayName = (): string => {
    if (!selectedPatient) return ''
    const mask = (value: string): string =>
      isHIPAAView ? maskString(value) : value
    return [
      selectedPatient.first_name,
      selectedPatient.middle_name,
      selectedPatient.last_name,
    ]
      .filter((part): part is string => hasMeaningfulValue(part))
      .map((part) => mask(part))
      .join(' ')
  }

  const displayName = getPatientDisplayName()

  const getInsuranceTooltipContent = (): React.ReactElement | string => {
    if (!selectedPatient?.insurance_provider) {
      return 'No insurance'
    }

    if (!hasMeaningfulValue(selectedPatient.insurance_provider)) {
      return 'No insurance information'
    }

    if (
      selectedPatient.pat_ins &&
      Array.isArray(selectedPatient.pat_ins) &&
      selectedPatient.pat_ins.length > 0
    ) {
      const carriers = selectedPatient.pat_ins
        .map((patIns: any) => {
          if (patIns.InsurancePlan?.carrier) {
            return patIns.InsurancePlan.carrier
          }
          if (patIns.carrier) {
            return patIns.carrier
          }
          return null
        })
        .filter(
          (carrier: string | null): carrier is string =>
            carrier !== null && hasMeaningfulValue(carrier),
        )

      const uniqueCarriers = Array.from(new Set(carriers))

      if (uniqueCarriers.length === 0) {
        return selectedPatient.insurance_provider
      }

      if (uniqueCarriers.length === 1) {
        return uniqueCarriers[0]
      }

      return (
        <>
          {uniqueCarriers.map((carrier, index) => (
            <div key={index}>{carrier}</div>
          ))}
        </>
      )
    }

    const providers = selectedPatient.insurance_provider
      .split(',')
      .map((p) => p.trim())
      .filter((p) => hasMeaningfulValue(p))

    if (providers.length === 0) {
      return 'No insurance information'
    }

    if (providers.length === 1) {
      return providers[0]
    }

    return (
      <>
        {providers.map((provider, index) => (
          <div key={index}>{provider}</div>
        ))}
      </>
    )
  }

  useEffect(() => {
    dispatch(fetchAllergiesCatalog())
    dispatch(fetchMedicalConditionsCatalog())
  }, [dispatch])

  useEffect(() => {
    if (!selectedPatient) {
      dispatch(toggleSidePanel(false))
    }
  }, [selectedPatient])

  useEffect(() => {
    if (selectedPatient) {
      if (shouldUsePatientTrayMockData(selectedPatient.patient_id)) {
        return
      }
      setIsPatientDetailsLoading(true)
      fetchPatientById(selectedPatient.patient_id, true)
        .then((res) => {
          if (res.statusCode !== 200 || !res.data?.patient) {
            return
          }
          const contact = contacts?.find(
            (c) =>
              c.patient_id ===
              (res.data.patient as PatientWithInsurance).patient_id,
          )
          dispatch(
            setSelectedPatient({ patient: res.data.patient as any, contact }),
          )
        })
        .catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('API call failed (backend not available):', error)
          }
        })
        .finally(() => setIsPatientDetailsLoading(false))
    }
  }, [refresh])

  useEffect(() => {
    const patientId = selectedPatient?.patient_id
    if (!patientId || isMockPatientId(patientId)) {
      setMembershipSidePanelOverview(null)
      return
    }

    let cancelled = false
    setMembershipSidePanelOverview(null)
    void dispatch(getMembershipMembers({ patient_id: patientId })).then(
      (result) => {
        if (cancelled) return
        if (!getMembershipMembers.fulfilled.match(result)) {
          setMembershipSidePanelOverview(null)
          return
        }

        const member = result.payload.members[0]
        setMembershipSidePanelOverview(
          member ? getMembershipOverviewForMember(member) : null,
        )
      },
    )

    return () => {
      cancelled = true
    }
  }, [dispatch, selectedPatient?.patient_id])

  const handleCallClick = (): void => {
    const patientContact = contactsHooks.filteredContacts.find(
      (contact) => contact.patient_id === selectedPatient?.patient_id,
    )
    if (patientContact) {
      contactsHooks.handlePhoneAction(patientContact)
    }
  }

  const handleCallerModalClose = (): void => {
    setShowCallerModal(false)
  }

  const handleEmailClick = (): void => {
    setShowEmailSender(true)
  }

  const handleEmailSenderClose = (): void => {
    setShowEmailSender(false)
  }

  const handleMessageClick = (): void => {
    const patientContact = contactsHooks.filteredContacts.find(
      (contact) => contact.patient_id === selectedPatient?.patient_id,
    )
    if (patientContact) {
      contactsHooks.handleSendMessage(patientContact)
      navigate('/message')
    }
  }

  const getDisplayTab = (name: Tabs): React.ReactElement => {
    switch (name) {
      case Tabs.DETAILS:
        return (
          <Details
            patient={selectedPatient}
            handleSetSchedule={() => setIsSchedulingModalOpen(true)}
            viewFamilyMembers={() => setSelectedTab(Tabs.FAMILY_MEMBERS)}
            onEdit={() => setShowPatientModal(true)}
            isHIPAAView={isHIPAAView}
            showMembershipOverview
          />
        )
      case Tabs.HISTORY:
        return <History patient={selectedPatient} />
      case Tabs.PLAN:
        return <Treatment patient={selectedPatient} />
      case Tabs.INSURANCE:
        return <Insurance patient={selectedPatient} />
      case Tabs.FILES:
        return <FilesTab />
      case Tabs.FAMILY_MEMBERS:
        return (
          <FamilyMembers
            patient={selectedPatient}
            isLoading={isPatientDetailsLoading}
          />
        )
      case Tabs.PAYMENT_METHODS:
        return <PaymentMethods patient={selectedPatient} />
      default:
        return <div></div>
    }
  }

  const handleClosePatientModal = (): void => {
    setShowPatientModal(false)
  }

  const handleSaveAppointment = (
    apptDetails: Partial<AppointmentRequest>,
  ): void => {
    createAppointment(apptDetails).then(() => dispatch(refreshFromPatient()))
  }

  const handleSavePatient = async (patientData: Patient): Promise<void> => {
    if (patientData.patient_id) {
      await dispatch(updatePatient(patientData)).unwrap()
    }
  }

  const handleDeletePatient = (): void => {
    if (selectedPatient) {
      dispatch(removePatient(selectedPatient.patient_id)).then(() =>
        dispatch(toggleSidePanel(false)),
      )
    }
    setShowConfirmDeleteModal(false)
  }

  return (
    <>
      <div
        className={`patient-side-panel-wrapper ${isHidden ? 'patient-side-panel-wrapper-hidden' : ''}`}
      >
        {!isHidden && selectedPatient && (
          <div className="patient-sidepanel-expand-anchor">
            <Tooltip content="Expand" placement="bottom" portal>
              <button
                type="button"
                className="patient-sidepanel-expand-btn"
                aria-label="Open full patient profile"
                onClick={() => {
                  dispatch(closeSidePanel())
                  navigateToPatientProfile(
                    navigate,
                    selectedPatient.patient_id,
                    `${pathname}${search}`,
                  )
                }}
              >
                <span
                  className="patient-sidepanel-expand-btn-inner"
                  aria-hidden
                >
                  <span className="patient-sidepanel-expand-bar" />
                  <i className="ti ti-chevron-left" />
                </span>
              </button>
            </Tooltip>
          </div>
        )}
        <div
          className={`h-screen flex flex-col sp-bg-blue-100 patient-side-panel ${isHidden ? '' : 'open'}`}
          style={{
            width: isHidden ? '0' : '311px',
            border: isHidden ? 'none' : '1px solid var(--blue-200)',
          }}
        >
          <div
            className={'flex-none patient-personal-details sp-bg-blue-100'}
            style={{ display: isHidden ? 'none' : '' }}
          >
            <div className="patient-buttons">
              <div className="profile-picture">
                <Avatar
                  source={
                    selectedPatient?.photo_file?.file_path ??
                    selectedPatient?.photo ??
                    'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
                  }
                  name={displayName}
                  height={64}
                  width={64}
                  style={{ borderRadius: '12px' }}
                />
                <button
                  type="button"
                  className="unstyled-button profile-picture-overlay"
                  aria-label="Edit photo"
                  onClick={() => setShowEditPhotoModal(true)}
                >
                  <span className="overlay-content">
                    <i className="mgc_pencil_fill" aria-hidden></i>
                    <span>Edit photo</span>
                  </span>
                </button>
              </div>
              <div style={{ width: '100%', marginLeft: '8px' }}>
                <div className="name-and-buttons">
                  <div className="name">
                    <div className="patient-name">{displayName}</div>
                  </div>
                  <div className="patient-sidepanel-header-actions">
                    {selectedPatient && (
                      <button
                        type="button"
                        className="patient-sidepanel-header-action"
                        aria-label="Open full patient profile"
                        title="Expand"
                        onClick={() => {
                          dispatch(closeSidePanel())
                          navigateToPatientProfile(
                            navigate,
                            selectedPatient.patient_id,
                            `${pathname}${search}`,
                          )
                        }}
                      >
                        <ArrowExpandIcon aria-hidden />
                      </button>
                    )}
                    <button
                      type="button"
                      className="patient-sidepanel-header-action"
                      aria-label="Close patient side panel"
                      title="Close"
                      onClick={toggle}
                    >
                      <PanelCloseIcon aria-hidden />
                    </button>
                  </div>
                </div>
                {false && (
                  <div>
                    <span className="schedule-status">
                      {false && (
                        <i className="ti ti-rosette-discount-check-filled member-badge-icon"></i>
                      )}{' '}
                      {'Member'}
                    </span>
                  </div>
                )}
                <div className="patient-bday-gender">
                  <div className="bday-text">
                    {selectedPatient && selectedPatient.date_of_birth !== ''
                      ? `${moment(selectedPatient.date_of_birth).format('MM/DD/YYYY')} (${getAge(selectedPatient.date_of_birth)} years)`
                      : ''}
                  </div>
                  <div className="bday-icon">
                    {genderIcon(selectedPatient?.gender || '')}
                  </div>
                </div>
              </div>
            </div>
            <div className="patient-personal-info">
              <div className="patient-action-buttons">
                {(() => {
                  const conditions =
                    getPatientMedicalConditionTitles(selectedPatient)
                  const hasMedical = conditions.length > 0
                  const medicalIcon = (
                    <div
                      className={`icon-tray icon-tray-heart ${hasMedical ? '' : 'is-empty'}`}
                    >
                      <i className="mgc_heart_fill"></i>
                    </div>
                  )
                  return (
                    <TooltipComponent
                      content={() =>
                        hasMedical
                          ? conditions.map((condition, index) => (
                              <div key={index}>{condition}</div>
                            ))
                          : 'No medical conditions'
                      }
                      opensOn="Hover"
                      cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                      position="BottomCenter"
                    >
                      {medicalIcon}
                    </TooltipComponent>
                  )
                })()}

                {(() => {
                  const allergies = getPatientAllergyTitles(selectedPatient)
                  const hasAllergies = allergies.length > 0
                  const allergiesIcon = (
                    <div
                      className={`icon-tray icon-tray-alert ${hasAllergies ? '' : 'is-empty'}`}
                    >
                      <i className="mgc_virus_fill"></i>
                    </div>
                  )
                  return (
                    <TooltipComponent
                      content={() =>
                        hasAllergies
                          ? allergies.map((allergy, index) => (
                              <div key={index}>{allergy}</div>
                            ))
                          : 'No allergies'
                      }
                      opensOn="Hover"
                      cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                      position="BottomCenter"
                    >
                      {allergiesIcon}
                    </TooltipComponent>
                  )
                })()}

                <TooltipComponent
                  content={getInsuranceTooltipContent}
                  opensOn="Hover"
                  cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                  position="BottomCenter"
                >
                  <div className="icon-tray icon-tray-shield">
                    <InsuranceVerifiedIcon
                      className="icon-tray-shield-icon"
                      aria-hidden
                    />
                  </div>
                </TooltipComponent>

                <TooltipComponent
                  content={certificateTooltipContent}
                  opensOn="Hover"
                  cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                  position="BottomCenter"
                >
                  <div className="icon-tray icon-tray-certificate">
                    <CertificateVerifiedIcon
                      className="icon-tray-certificate-icon"
                      aria-hidden
                    />
                  </div>
                </TooltipComponent>
              </div>
              <div className="patient-action-buttons">
                <TooltipComponent
                  content="Schedule appointment"
                  opensOn="Hover"
                  cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                  position="TopCenter"
                >
                  <button
                    type="button"
                    className="unstyled-button icon-tray"
                    aria-label="Schedule appointment"
                    onClick={() => setIsSchedulingModalOpen(true)}
                  >
                    <CalendarAddIcon
                      className="icon-tray-calendar-add-icon"
                      aria-hidden
                    />
                  </button>
                </TooltipComponent>
                <TooltipComponent
                  content="Call"
                  opensOn="Hover"
                  cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                  position="TopCenter"
                >
                  <button
                    type="button"
                    className="unstyled-button icon-tray"
                    aria-label="Call"
                    onClick={handleCallClick}
                  >
                    <PhoneIcon className="icon-tray-phone-icon" aria-hidden />
                  </button>
                </TooltipComponent>
                <TooltipComponent
                  content="Message"
                  opensOn="Hover"
                  cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                  position="TopCenter"
                >
                  <button
                    type="button"
                    className="unstyled-button icon-tray"
                    aria-label="Message"
                    onClick={handleMessageClick}
                  >
                    <i className="mgc_message_4_fill" aria-hidden></i>
                  </button>
                </TooltipComponent>
                <TooltipComponent
                  content="Email"
                  opensOn="Hover"
                  cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                  position="TopCenter"
                >
                  <button
                    type="button"
                    className="unstyled-button icon-tray"
                    aria-label="Email"
                    onClick={handleEmailClick}
                  >
                    <i className="ti ti-mail-filled" aria-hidden></i>
                  </button>
                </TooltipComponent>
              </div>
            </div>
          </div>
          <div className="sp-main-bg-outer">
            <div className="sp-main-bg">
              <div
                className="flex-none patient-tabs"
                style={{ display: isHidden ? 'none' : '' }}
              >
                <ButtonGroup className="patient-btn m-0">
                  {listofTabs.map((tab) => (
                    <Btn
                      variant="secondary"
                      onClick={() => setSelectedTab(tab.name)}
                      key={tab.name}
                      className={`${tab.name === selectedTab ? 'patient-btn-item' : ''}`}
                    >
                      <TooltipComponent
                        content={tab.tooltip}
                        opensOn="Hover"
                        cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                        position="TopCenter"
                      >
                        {tab.SvgIcon ? (
                          <tab.SvgIcon
                            className={`sp-sidepanel-tab-svg-icon ${
                              tab.name === selectedTab
                                ? 'selected'
                                : 'unselected'
                            }`}
                            aria-hidden
                          />
                        ) : (
                          <i
                            className={`${
                              tab.name === selectedTab
                                ? `${tab.selected} selected`
                                : `${tab.unselected} unselected`
                            }`}
                          ></i>
                        )}
                      </TooltipComponent>
                    </Btn>
                  ))}
                </ButtonGroup>
              </div>
              <div className="patient-content-container">
                <div
                  className=" patient-content"
                  style={{
                    display: isHidden ? 'none' : '',
                  }}
                  key={selectedPatient?.patient_id}
                >
                  {getDisplayTab(selectedTab)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CallerModal
        {...noteOverlayAppModalBootstrapProps}
        show={showCallerModal}
        onHide={handleCallerModalClose}
        phoneNumber={selectedPatient?.cellphone ?? ''}
      />
      <EmailSender
        {...noteOverlayAppModalBootstrapProps}
        show={showEmailSender}
        onHide={handleEmailSenderClose}
        recipientEmail={selectedPatient?.email ?? ''}
      />
      {isSchedulingModalOpen && (
        <ScheduleAppointment
          {...noteOverlayAppModalBootstrapProps}
          modalId="schedule-appointment-modal-stacked"
          isOpen={isSchedulingModalOpen}
          handleModal={() => setIsSchedulingModalOpen(false)}
          onSave={handleSaveAppointment}
          initialStartTime={moment().format('YYYY-MM-DD')}
          initialEndTime={moment().format('YYYY-MM-DD')}
          initialRoom={undefined}
          initialProvider={undefined}
          currentAppt={undefined}
          {...(selectedPatient && {
            preSelectedPatient: {
              patient_id: selectedPatient.patient_id,
              first_name: selectedPatient.first_name,
              last_name: selectedPatient.last_name,
              has_insurance: selectedPatient.has_insurance,
              pat_ins: selectedPatient.pat_ins,
            } as PatientWithInsurance,
          })}
        />
      )}
      {showPatientModal && (
        <AddPatientModal
          {...noteOverlayAppModalBootstrapProps}
          isOpen={showPatientModal}
          onRequestClose={handleClosePatientModal}
          onSave={handleSavePatient}
          patientData={selectedPatient ?? undefined}
        />
      )}
      <ConfirmDeleteModal
        show={showConfirmDeleteModal}
        onHide={() => setShowConfirmDeleteModal(false)}
        onConfirm={handleDeletePatient}
        entityName={
          selectedPatient ? getFullName(selectedPatient, true, isHIPAAView) : ''
        }
      />
      {contactsHooks.showSelectContactModal && (
        <SelectContactModal
          {...noteOverlayAppModalBootstrapProps}
          show={contactsHooks.showSelectContactModal}
          onHide={contactsHooks.handleCloseSelectContactModal}
          contactData={contactsHooks.selectedContact as IContact}
        />
      )}
      {showEditPhotoModal && (
        <EditPhoto
          {...noteOverlayAppModalBootstrapProps}
          show={showEditPhotoModal}
          onHide={() => setShowEditPhotoModal(false)}
          entityId={selectedPatient?.patient_id ?? ''}
        />
      )}
    </>
  )
}

export default SidePanel
